import { useState } from 'react';
import { threatFalsePositive, threatMitigate, threatsList, type ActiveThreat } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';

// Restored from the Flutter Incidents tab (incidents_view.dart): each active threat, with
// Mitigate and False positive. The V3 design only counts active threats (its tile opens
// this tab), so the rows are drawn here, in the tab's own style, and only when a threat is
// active. Both answers are sealed to the Truth Ledger by the server.

const SEV: Record<string, [string, string]> = {
  critical: ['#8C3A33', 'rgba(179,38,30,.14)'],
  high: ['#B3261E', 'rgba(179,38,30,.10)'],
  medium: ['#8A5A00', 'rgba(165,86,26,.12)'],
  low: ['#54606C', 'rgba(34,39,46,.07)'],
};
const BTN = 'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;';

const when = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

export function ActiveThreats() {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const q = useQuery((signal) => threatsList(client, { signal }), [client]);
  const [ask, setAsk] = useState<{ t: ActiveThreat; as: 'mitigated' | 'false_positive' } | null>(null);
  const rows = q.data?.active ?? [];
  if (!rows.length) return null;
  return (
    <div data-testid="oversight-threats" style={sx('flex:0 0 auto;min-width:0;border-top:1px solid rgba(34,39,46,.14);padding-top:8px;')}>
      <div style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>{`Active threats · ${rows.length}`}</div>
      {rows.map((t) => {
        const c = SEV[t.severity] ?? SEV.low;
        return (
          <div key={t.id} style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
            <span style={sx(`flex:0 0 auto;display:inline-flex;align-items:center;height:18px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;color:${c[0]};background:${c[1]};`)}>{t.severity.toUpperCase()}</span>
            <span style={sx('flex:1 1 260px;min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;text-wrap:pretty;')}>{t.description}</span>
              <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>{`${t.type.replace(/_/g, ' ')} · ${t.actorId} · ${when(t.detectedAt)}`}</span>
            </span>
            <a href="#" aria-disabled={!!roReason || undefined} title={roReason ?? undefined} onClick={(e) => { e.preventDefault(); if (!roReason) setAsk({ t, as: 'mitigated' }); }} style={sx(BTN + 'background:#2F5C97;color:#fff;')}>
              Mitigate
            </a>
            <a href="#" aria-disabled={!!roReason || undefined} title={roReason ?? undefined} onClick={(e) => { e.preventDefault(); if (!roReason) setAsk({ t, as: 'false_positive' }); }} style={sx(BTN + 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.2);')}>
              False positive
            </a>
          </div>
        );
      })}
      {ask && (
        <ConfirmDialog
          title={ask.as === 'mitigated' ? 'Mark this threat mitigated?' : 'Mark this threat a false positive?'}
          body={`${ask.t.description}. It leaves the active list and is sealed to the Truth Ledger with the time.`}
          confirmLabel={ask.as === 'mitigated' ? 'Mitigated' : 'False positive'}
          onConfirm={async () => {
            if (ask.as === 'mitigated') await threatMitigate(client, { params: { id: ask.t.id } });
            else await threatFalsePositive(client, { params: { id: ask.t.id } });
            q.reload();
          }}
          onClose={() => setAsk(null)}
        />
      )}
    </div>
  );
}
